export type LinearRGB = readonly [number, number, number];
export type Lab = readonly [number, number, number];

export function srgbChannelToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function linearChannelToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

export function hexToLinear(hex: string): LinearRGB {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return [srgbChannelToLinear(r), srgbChannelToLinear(g), srgbChannelToLinear(b)];
}

export function linearToHex(c: LinearRGB): string {
  const toHex = (v: number) => {
    const srgb = linearChannelToSrgb(v);
    const byte = Math.round(Math.max(0, Math.min(1, srgb)) * 255);
    return byte.toString(16).padStart(2, '0');
  };
  return `#${toHex(c[0])}${toHex(c[1])}${toHex(c[2])}`;
}

function linearToXyz(c: LinearRGB): readonly [number, number, number] {
  const [r, g, b] = c;
  const x = 0.4124564 * r + 0.3575761 * g + 0.1804375 * b;
  const y = 0.2126729 * r + 0.7151522 * g + 0.0721750 * b;
  const z = 0.0193339 * r + 0.1191920 * g + 0.9503041 * b;
  return [x, y, z];
}

function xyzToLab(xyz: readonly [number, number, number]): Lab {
  const refX = 0.95047;
  const refY = 1.0;
  const refZ = 1.08883;

  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);

  const fx = f(xyz[0] / refX);
  const fy = f(xyz[1] / refY);
  const fz = f(xyz[2] / refZ);

  const L = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const bVal = 200 * (fy - fz);

  return [L, a, bVal];
}

export function srgb8ToLab(r: number, g: number, b: number): Lab {
  const linear: LinearRGB = [
    srgbChannelToLinear(r / 255),
    srgbChannelToLinear(g / 255),
    srgbChannelToLinear(b / 255),
  ];
  return xyzToLab(linearToXyz(linear));
}

export function linearToLab(c: LinearRGB): Lab {
  return xyzToLab(linearToXyz(c));
}

export function deltaE76(a: Lab, b: Lab): number {
  const dL = a[0] - b[0];
  const da = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt(dL * dL + da * da + db * db);
}

export function luminance(c: LinearRGB): number {
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function scaleColor(c: LinearRGB, k: number): LinearRGB {
  return [c[0] * k, c[1] * k, c[2] * k];
}
